<template>
<m-pageframe
    :title="title"
>
<v-container
    fluid
    style="padding-left: 30px; padding-right: 30px"
>
    <h2>What is Form Connect?</h2>
    <div
        class="section"
    >
        Form Connect allows a web designer to connect forms on your website to RedChirp.  
        
        When a customer completes the form, a contact request, including the information provided on the form will be submitted to this inbox ({{inbox_label}}).
    </div>

    <h2>How do I setup Form Connect</h2>
    <div
        class="section"
    >
        <p>It's simple!</p>
        <p>You post form data to RedChirp servers using AJAX.  For this inbox ({{inbox_label}}) you post to:</p>

        <m-code-block
            style="padding: 10px; margin-bottom: 15px;"
        >
            
            {{$rc.getAPIEndpoint('',false)}}contact-requests/form-connect/{{$route.params.organization_id}}/{{$route.params.inbox_id}}
            
        </m-code-block>

        <p>The json you post to that URL requires only two fields: <span class="code">first_name</span>, <span class="code">mobile</span>.</p>
        <p>You can, optionally, include <span class="code">last_name</span>, <span class="code">submitted_from_url</span> and an array, <span class="code">extra_fields</span>.</p>

        <a href="https://jsfiddle.net/jim_kane/tx25kbey/10/" target="_blank">Here is some sample code to get you started (jsfiddle)</a>
    </div>

    <h2>Testing</h2>
    <div
        class="section"
    >
        <p>Posting to the above URL will cause a submission to this inbox (<a v-on:click="onClickViewEvents()"><v-icon>mdi-open-in-new</v-icon> Click here to view the form submission events associated with this inbox.</a>).</p>
        
        By default, your integration will be in <strong>test</strong> mode.  This means the <em>only</em> thing RedChirp will do when the form is submitted is add the post to the log below. 
        (it will not actually create a contact request, start the notification plan or take any other action)  To exit test mode add a field to your json <span class="code">production</span> and set its value to <span class="code">true</span>.
    </div>

    <h2>Frequently Asked Questions</h2>
    <div
        class="section"
    >
        <b>Question:</b> 
        <blockquote>Can multiple forms submit to the same inbox?</blockquote>
        <b>Answer:</b>
        <blockquote>Yes!</blockquote>

        <b>Question:</b> 
        <blockquote>How can I track which form submitted each contact request?</blockquote>
        <b>Answer:</b>
        <blockquote>
            There are several options.  

            <ul>
                <li>Create multiple inboxes and have each form post to its own inbox &mdash;  Since contact requests easily searchable by the inbox they were submitted to, this option makes it easy to tell which form each contact request is from.  <b>Note</b> it is easy to copy notification plans between inboxes, so this is fairly easy to setup as well.</li>
                <li>Use <span class="code">submitted_from_url</span> to tell which form submitted each contact request.  If your forms are on different pages, then this should be a straightforward way to tell the submissions apart.</li>
                <li>Add a field to the posted json.  If your purposes is simply to tell the form submissions apart for testing / debugging, then add any descriptive field you want to the json posted.  This data will show in the events (below) but no where else in the RedChirp software.</li>
            </ul>

        </blockquote>

        <b>Question:</b> 
        <blockquote>Can I create a form that submits to more than one inbox?</blockquote>
        <b>Answer:</b>
        <blockquote>
            <p>Yes!</p>
            
            <p>
            The most common use case for this is when you want the user entered content of the form submission to control which inbox the contact request is sent to.
            This can happen, for example, in a business with multiple locations where the user picks the location they want to use from a combo box.  
            </p>

            <p>
                The inbox that the contact request is created in is controlled by the URL that the json data is posted to.  Simply have your code select the appropriate url
                based on the form contents.  For example, imagine you have a select control the user picks their desired location from.  Set the inbox URL for each location
                as the value for the options of the select and then have your posting code use the URL selected.
            </p>
        </blockquote>

        <b>Question:</b> 
        <blockquote>Can I use form connect via REST?</blockquote>
        <b>Answer:</b>
        <blockquote>
            <p>Yes!</p>
            
            <p>
            Below is an example curl command that submittes a form request.  Don't forget to add <code>"production": true</code> before you go live. 
            </p>


        <m-code-block
                :click_anywhere_to_copy="false"
        >
curl -v -X POST {{$rc.getAPIEndpoint('',false)}}contact-requests/form-connect/{{$route.params.organization_id}}/{{$route.params.inbox_id}}  \<br/>
-H 'Content-Type: application/json; charset=utf-8' \<br/>
--data-binary @- &lt;&lt; EOF<br/>
{<br/>
   "first_name" : "Jane",<br/>
   "last_name" : "Doe",<br/>
   "mobile": "237-111-2222",<br/>

   "consent_specimen": "This is an example agreement -- do not use in production",<br/>
   "consent_conversational": true,<br/>
   "consent_informational": false,<br/>
   "consent_promotional": false<br/>
}<br/>
EOF        <br/>
        </m-code-block>

        </blockquote>

         <b>Question:</b> 
        <blockquote>How can I require a double opt-in via form connect?</blockquote>
        <b>Answer:</b>
        <blockquote>
            <p>Yes!</p>
            
            <p>
            Simple: Set the consent_double_opt_in, consent_double_opt_in_message, consent_double_opt_after_message and consent_double_opt_in_send_from varaible (the last must be a phone number in your RedChirp account).  Here is an example:
            </p>

        <m-code-block
                :click_anywhere_to_copy="false"
        >
curl -v -X POST {{$rc.getAPIEndpoint('',false)}}contact-requests/form-connect/{{$route.params.organization_id}}/{{$route.params.inbox_id}}  \<br/>
-H 'Content-Type: application/json; charset=utf-8' \<br/>
--data-binary @- &lt;&lt; EOF<br/>
{<br/>
   "first_name" : "Jane",<br/>
   "last_name" : "Doe",<br/>
   "mobile": "237-111-2222",<br/>

   "consent_specimen": "This is an example agreement -- do not use in production",<br/>
   "consent_conversational": true,<br/>
   "consent_informational": false,<br/>
   "consent_promotional": false,<br/>
   "consent_double_opt_in": true,<br/>
   "consent_double_opt_in_message": "Reply YES to opt in to messaging (this is an example, do not use in production)",<br/>
   "consent_double_opt_after_message": "You have opted in! (this is an example, do not use in production)",<br/>
   "consent_double_opt_in_send_from": "237-222-3333"<br/>
}<br/>
EOF        <br/>
        </m-code-block>

        </blockquote>

    <b>Question:</b> 
        <blockquote>Can I add extra fields using form</blockquote>
        <b>Answer:</b>
        <blockquote>
            <p>Yes!</p>
            
            <p>
            Below is an example curl command that submittes a form request.  Don't forget to add <code>"production": true</code> before you go live. 
            </p>


        <m-code-block
                :click_anywhere_to_copy="false"
        >
curl -v -X POST {{$rc.getAPIEndpoint('',false)}}contact-requests/form-connect/{{$route.params.organization_id}}/{{$route.params.inbox_id}}  \<br/>
-H 'Content-Type: application/json; charset=utf-8' \<br/>
--data-binary @- &lt;&lt; EOF<br/>
{<br/>
   "first_name" : "Jane",<br/>
   "last_name" : "Doe",<br/>
   "mobile": "237-111-2222",<br/>

   "consent_specimen": "This is an example agreement -- do not use in production",<br/>
   "consent_conversational": true,<br/>
   "consent_informational": false,<br/>
   "consent_promotional": false<br/>
}<br/>
EOF        <br/>
        </m-code-block>

        </blockquote>

         <b>Question:</b> 
        <blockquote>How can I require a double opt-in via form connect?</blockquote>
        <b>Answer:</b>
        <blockquote>
            <p>Yes!</p>
            
            <p>
            Simple: Set the consent_double_opt_in, consent_double_opt_in_message, consent_double_opt_after_message and consent_double_opt_in_send_from varaible (the last must be a phone number in your RedChirp account).  Here is an example:
            </p>

        <m-code-block
                :click_anywhere_to_copy="false"
        >
curl -v -X POST {{$rc.getAPIEndpoint('',false)}}contact-requests/form-connect/{{$route.params.organization_id}}/{{$route.params.inbox_id}}  \<br/>
-H 'Content-Type: application/json; charset=utf-8' \<br/>
--data-binary @- &lt;&lt; EOF<br/>
{<br/>
   "first_name" : "Jane",<br/>
   "last_name" : "Doe",<br/>
   "mobile": "237-111-2222",<br/>

   "consent_specimen": "This is an example agreement -- do not use in production",<br/>
   "consent_conversational": true,<br/>
   "consent_informational": false,<br/>
   "consent_promotional": false,<br/>
   "consent_double_opt_in": true,<br/>
   "consent_double_opt_in_message": "Reply YES to opt in to messaging (this is an example, do not use in production)",<br/>
   "consent_double_opt_after_message": "You have opted in! (this is an example, do not use in production)",<br/>
   "consent_double_opt_in_send_from": "237-222-3333"<br/>
}<br/>
EOF        <br/>
        </m-code-block>

        </blockquote>

        <b>Question:</b> 
        <blockquote>Can I submit extra fields via form connect?</blockquote>
        <b>Answer:</b>
        <blockquote>
            <p>Yes!</p>
            
            <p>
            Simple: Just set extra_fields to an array containing your extra field(s).  Each extra field should have a text field and a value field.  Here is an example:
            </p>

        <m-code-block
                :click_anywhere_to_copy="false"
        >
curl -v -X POST {{$rc.getAPIEndpoint('',false)}}contact-requests/form-connect/{{$route.params.organization_id}}/{{$route.params.inbox_id}}  \<br/>
-H 'Content-Type: application/json; charset=utf-8' \<br/>
--data-binary @- &lt;&lt; EOF<br/>
{<br/>
   "first_name" : "Jane",<br/>
   "last_name" : "Doe",<br/>
   "mobile": "237-111-2222",<br/>

   "consent_specimen": "This is an example agreement -- do not use in production",<br/>
   "consent_conversational": true,<br/>
   "consent_informational": false,<br/>
   "consent_promotional": false,<br/>
   extra_fields: [<br/>
    &nbsp;&nbsp;&nbsp;&nbsp;{ "text": "How many cookies are in the jar?", "value": "42" },<br/>
    &nbsp;&nbsp;&nbsp;&nbsp;{ "text": "How many apples were in the basket?", "value": "99" }<br/>
  ]<br/>
}<br/>
EOF<br/>

        </m-code-block>

        </blockquote>


    </div>




    <h2>Events</h2>

    <div
        class="section"
        style="margin-top: 15px"
    >
        <a v-on:click="onClickViewEvents()"><v-icon>mdi-open-in-new</v-icon> Click here to view the form submission events associated with this inbox.</a>
    </div>


</v-container>
</m-pageframe>
</template>

<script>
const Time = require('@/js/time.js');
const _ = require('lodash');

export default {
    
    data() 
    {
        return {
            profile: null,
            inbox: null,
        };
    },

    mounted () {
        this.loadOrganizationProfile();
    },

    methods: {

        onClickViewEvents()
        {
            let url = this.$router.resolve({
                'name': 'events',
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                },

                query: {
                    'contact_request_inbox_id': this.$route.params.inbox_id,
                },
            });

            window.open(url.href,'_blank');
        },

        loadOrganizationProfile() {
            this.$rc.showSpinner();
            
            this.$rc.getOrganizationProfile(
                this.$route.params.organization_id,
            
                (data) => {
                    this.$rc.hideSpinner();
                    this.profile = data;

                    for ( let i = 0; i < data.organization_account.inboxes.length; i++ )
                    {
                        let inbox = data.organization_account.inboxes[i];

                        if ( inbox.inbox_id == this.$route.params.inbox_id )
                        {
                            this.inbox = inbox;
                        }
                    }
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to complete API call');
                },
            );
        },

        prettyPrintTime(log)
        {
            return Time.getTimeAndDate(log.time);
        },

        prettyLogEntry(log)
        {
            let ret = log.log_entry;

            ret = '<br/>'+ret.replaceAll('\n','<br/>');

            return ret;
        },
    },

    computed: {
        title() {
            if ( this.inbox == null ) return 'Form Connect';
            return 'Form Connect: '+this.inbox.internal_label;
        },

        inbox_label() {
            if ( this.inbox == null ) return '';
            return this.inbox.internal_label;
        }
    },

}
</script>

<style lang="scss" scoped>

div.section
{
    margin-bottom: 15px;
}

span.code
{
    font-family: 'Courier New', Courier, monospace;
}

blockquote
{
    margin-left: 15px;
    margin-top: 15px;
    margin-bottom: 15px;
}

</style>